if (!customElements.get('countdown-timer-bar')) {
  class CountdownBar extends HTMLElement {
    constructor() {
      super();
      this.interval = null;
      this.rotationInterval = null;
      this.intersectionObserver = null;
      this.updateLayoutHeights = null;
      this.section = this.closest('.section-countdown-timer-bar');
      this.startCountdownTimer();
      this.tick();
      this.syncRotation();
      this.calcHeight();
    }

    disconnectedCallback() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
      if (this.rotationInterval) {
        clearInterval(this.rotationInterval);
        this.rotationInterval = null;
      }
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    }

    getSlides() {
      return Array.from(this.querySelectorAll('.countdown-timer-bar__slide'));
    }

    resolveSlideDateTime(slide) {
      if (slide.getAttribute('data-enable-mock-timer') === 'true') {
        const infiniteDays = Number(slide.getAttribute('data-enable-mock-picker-day')) || 2;
        const diffHours = Number(slide.getAttribute('data-enable-mock-picker-hours')) || 3;
        const now = new Date();
        const futureDate = new Date(
          now.getTime() + (infiniteDays * 24 + diffHours) * 60 * 60 * 1000,
        );
        const year = futureDate.getFullYear();
        const month = String(futureDate.getMonth() + 1).padStart(2, '0');
        const day = String(futureDate.getDate()).padStart(2, '0');
        const hours = String(futureDate.getHours()).padStart(2, '0');
        const minutes = String(futureDate.getMinutes()).padStart(2, '0');
        return { userDate: `${year}-${month}-${day}`, userTime: `${hours}:${minutes}` };
      }
      return {
        userDate: slide.getAttribute('data-date') || '',
        userTime: slide.getAttribute('data-time') || '',
      };
    }

    processSlide(slide) {
      const completed = slide.getAttribute('data-completed');
      const isMock = slide.getAttribute('data-enable-mock-timer') === 'true';
      const endUnixAttr = slide.getAttribute('data-end-unix');
      let distance;

      if (!isMock && endUnixAttr != null && endUnixAttr !== '') {
        const endMs = Number(endUnixAttr) * 1000;
        distance = endMs - Date.now();
        if (!Number.isFinite(distance)) {
          const { userDate, userTime } = this.resolveSlideDateTime(slide);
          const countdownDate = new Date(`${userDate}T${userTime}`);
          distance = countdownDate.getTime() - Date.now();
        }
      } else {
        const { userDate, userTime } = this.resolveSlideDateTime(slide);
        const countdownDate = new Date(`${userDate}T${userTime}`);
        distance = countdownDate.getTime() - Date.now();
      }

      if (distance < 0 && completed === 'hide_section') {
        slide.remove();
        return 'removed';
      }

      if (distance < 0 && completed === 'show_text') {
        if (slide.getAttribute('data-expired') !== 'true') {
          slide.setAttribute('data-expired', 'true');
          const mainEl = slide.querySelector('.countdown-timer-bar__main');
          const endEl = slide.querySelector('.countdown-timer-bar__end');
          if (mainEl) mainEl.style.display = 'none';
          if (endEl) endEl.style.display = 'flex';
        }
        return 'expired_show_text';
      }

      const totalSeconds = Math.floor(distance / 1000);
      const totalHours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const hoursEl = slide.querySelector('.countdown-timer-bar__hours');
      const minutesEl = slide.querySelector('.countdown-timer-bar__minutes');
      const secondsEl = slide.querySelector('.countdown-timer-bar__seconds');

      const formattedHours = String(totalHours).padStart(2, '0');
      const formattedMinutes = String(minutes).padStart(2, '0');
      const formattedSeconds = String(seconds).padStart(2, '0');
      if (hoursEl) hoursEl.textContent = formattedHours;
      if (minutesEl) minutesEl.textContent = formattedMinutes;
      if (secondsEl) secondsEl.textContent = formattedSeconds;

      return 'ok';
    }

    tick() {
      const slidesBefore = this.getSlides();
      let removed = false;
      slidesBefore.forEach((slide) => {
        const result = this.processSlide(slide);
        if (result === 'removed') removed = true;
      });

      const slides = this.getSlides();
      if (slides.length === 0) {
        if (this.section) this.section.style.display = 'none';
        document.documentElement.style.setProperty('--countdown-height', '0px');
        document.documentElement.style.setProperty('--countdown-bar-height', '0px');
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }
        if (this.rotationInterval) {
          clearInterval(this.rotationInterval);
          this.rotationInterval = null;
        }
        return;
      }

      this.setAttribute('data-slide-count', String(slides.length));

      if (removed) {
        if (!slides.some((s) => s.classList.contains('is-active'))) {
          slides[0].classList.add('is-active');
        }
        this.syncRotation();
        if (this.updateLayoutHeights) this.updateLayoutHeights();
      }
    }

    startCountdownTimer() {
      if (this.interval) clearInterval(this.interval);
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    rotateToNext() {
      const slides = this.getSlides();
      if (slides.length <= 1) return;
      const activeIdx = slides.findIndex((s) => s.classList.contains('is-active'));
      const fromIdx = activeIdx >= 0 ? activeIdx : 0;
      const nextIdx = (fromIdx + 1) % slides.length;
      slides.forEach((s) => s.classList.remove('is-active'));
      slides[nextIdx].classList.add('is-active');
    }

    syncRotation() {
      if (this.rotationInterval) {
        clearInterval(this.rotationInterval);
        this.rotationInterval = null;
      }
      const slides = this.getSlides();
      const ms = Number(this.getAttribute('data-rotate-interval')) || 0;
      if (slides.length > 1 && ms > 0) {
        this.rotationInterval = window.setInterval(() => this.rotateToNext(), ms);
      }
    }

    calcHeight() {
      const updateHeights = () => {
        if (!this.isConnected) return;

        const rect = this.getBoundingClientRect();
        const actualHeight = rect.height;
        const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;

        if (actualHeight > 0) {
          document.documentElement.style.setProperty('--countdown-height', `${actualHeight}px`);
        } else {
          document.documentElement.style.setProperty('--countdown-height', '0px');
        }

        if (isVisible) {
          const visibleTop = Math.max(0, rect.top);
          const visibleBottom = Math.min(window.innerHeight, rect.bottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);

          if (visibleHeight > 0) {
            document.documentElement.style.setProperty('--countdown-bar-height', `${visibleHeight}px`);
          } else {
            document.documentElement.style.setProperty('--countdown-bar-height', '0px');
          }
        } else {
          document.documentElement.style.setProperty('--countdown-bar-height', '0px');
        }
      };

      this.updateLayoutHeights = updateHeights;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const visibleHeight = entry.intersectionRect.height;
              const actualHeight = entry.boundingClientRect.height;

              if (actualHeight > 0) {
                document.documentElement.style.setProperty('--countdown-height', `${actualHeight}px`);
              }

              if (visibleHeight > 0) {
                document.documentElement.style.setProperty('--countdown-bar-height', `${visibleHeight}px`);
              } else {
                document.documentElement.style.setProperty('--countdown-bar-height', '0px');
              }
            } else {
              const actualHeight = entry.boundingClientRect.height;
              if (actualHeight > 0) {
                document.documentElement.style.setProperty('--countdown-height', `${actualHeight}px`);
              }
              document.documentElement.style.setProperty('--countdown-bar-height', '0px');
            }
          });
        },
        {
          threshold: [0, 0.01, 0.1, 0.5, 1],
          rootMargin: '0px',
        },
      );

      this.intersectionObserver = observer;
      observer.observe(this);

      updateHeights();

      let scrollTimeout;
      window.addEventListener(
        'scroll',
        () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            updateHeights();
          }, 10);
        },
        { passive: true },
      );

      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          updateHeights();
        }, 100);
      });
    }
  }

  customElements.define('countdown-timer-bar', CountdownBar);
}
